import { appConfig } from "@/app/module_api";
import { runBootstrap } from "@/app/module_api";
import {
  crypto as switchCrypto,
  decodeBase64,
  encodeBase64,
  linkedDeviceFieldDeviceId,
  linkedDeviceFieldKind,
  linkedDeviceFieldPublicKey,
  linkedDeviceFieldSecretKey,
  linkedDeviceFieldMlKemPublicKey,
  linkedDeviceFieldMlKemSecretKey,
  linkedDeviceFieldTargetDeviceId,
  linkedDeviceFieldTempPublicKey,
  linkedDeviceKeySyncKind,
  linkedDeviceNotificationKind,
  parseJwtPayload,
  prepareDMMessage,
  setToken,
  WebSocketEventType,
} from "@/core_bridge/module_api";
import { db } from "@/services/module_api";
import { send as sendWebSocket } from "@/transport/webSocketService";
import { useStore } from "@/state/legacy_module_api";
import { extractContactPublicKeys } from "@/data/adapters/contact_keys";

export type LinkedDevice = {
  deviceId: string;
  platform: string;
  status: string;
  linkedAt: number;
  lastSeenAt?: number | null;
  isPrimary: boolean;
  isCurrent: boolean;
};

export type DeviceLinkStartResult = {
  code: string;
  linkId: string;
  expiresAt: number;
};

export type DeviceLinkCompleteResult = {
  success: boolean;
  token: string;
  deviceId: string;
  status: string;
  tempKeyPair: TempKeyPair;
};

export type TempKeyPair = {
  publicKey: string;
  secretKey: string;
};

type DeviceLinkedNotification = {
  kind: string;
  deviceId: string;
  tempPublicKey?: string | null;
};

type KeySyncControlPayload = {
  kind: string;
  publicKey: string;
  secretKey: string;
  mlKemPublicKey?: string;
  mlKemSecretKey?: string;
};

function buildBasicAuthHeader(token: string): string {
  return `Basic ${btoa(`switch:${token}`)}`;
}

function createTemporaryKeyPair(): TempKeyPair {
  const keyPair = switchCrypto.boxKeypair();
  return {
    publicKey: encodeBase64(keyPair.public_key),
    secretKey: encodeBase64(keyPair.secret_key),
  };
}

async function installKeyPair(keyPair: TempKeyPair): Promise<void> {
  const store = useStore.getState() as any;
  console.info("[deviceLinking] installing temp keypair", {
    publicKeyPrefix: keyPair.publicKey.slice(0, 12),
    secretKeyPrefix: keyPair.secretKey.slice(0, 12),
  });
  await db.updateKeyPair(keyPair.publicKey, keyPair.secretKey);
  store.setKeyPair?.({
    cryptoKeys: {
      publicKey: decodeBase64(keyPair.publicKey),
      secretKey: decodeBase64(keyPair.secretKey),
    },
    base64: {
      publicKey: keyPair.publicKey,
      secretKey: keyPair.secretKey,
    },
  });
}

async function hydrateSelfUserFromToken(token: string): Promise<void> {
  const claims = parseJwtPayload(token);
  const userId = claims?.user_id;
  const username = claims?.username;
  const discriminator = claims?.discriminator;
  const fullHandle =
    claims?.full_handle ||
    (username && discriminator ? `${username}.${discriminator}` : undefined);

  if (!userId || !fullHandle) {
    console.warn("[deviceLinking] missing claims for self hydration", {
      hasUserId: !!userId,
      hasFullHandle: !!fullHandle,
    });
    return;
  }

  try {
    const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/find-user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: buildBasicAuthHeader(token),
      },
      body: JSON.stringify({ handle: fullHandle }),
    });
    const result = await parseResponse<any>(response);
    const userData = result?.user || {};
    const publicKey =
      userData.publicKey ||
      userData.public_key ||
      userData.publicKeys?.[0]?.publicKey ||
      userData.public_keys?.[0]?.public_key ||
      null;

    console.info("[deviceLinking] hydrated self user before realtime bootstrap", {
      userId,
      fullHandle,
      hasPublicKey: !!publicKey,
      publicKeyCount:
        userData.publicKeys?.length ??
        userData.public_keys?.length ??
        0,
    });

    useStore.getState().setUser?.({
      userId,
      username: (result as any)?.username || username,
      discriminator: (result as any)?.discriminator || discriminator,
      fullHandle,
      publicKey,
      publicKeys:
        userData.publicKeys ||
        userData.public_keys ||
        (publicKey ? [{ deviceId: userData.primaryDeviceId || "primary", publicKey }] : undefined),
      isBlocked: false,
      isMuted: false,
    } as any);
  } catch (error) {
    console.warn("[deviceLinking] failed to hydrate self user before realtime bootstrap", error);
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  let payload: any = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
  }

  return payload as T;
}

export async function startDeviceLink(token: string): Promise<DeviceLinkStartResult> {
  const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/device-link/start`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: buildBasicAuthHeader(token),
    },
    body: JSON.stringify({}),
  });

  return parseResponse<DeviceLinkStartResult>(response);
}

export async function completeDeviceLink(code: string): Promise<DeviceLinkCompleteResult> {
  const trimmedCode = code.trim();
  if (!trimmedCode) {
    throw new Error("Enter a valid link code");
  }

  const contract = linkedDeviceContract();
  const tempKeyPair = createTemporaryKeyPair();
  console.info("[deviceLinking] completing web device link", {
    codeLength: trimmedCode.length,
    tempPublicKeyPrefix: tempKeyPair.publicKey.slice(0, 12),
  });
  const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/device-link/complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      code: trimmedCode,
      appId: appConfig.appId,
      platform: "web",
      [contract.tempPublicKeyField]: tempKeyPair.publicKey,
    }),
  });

  const result = await parseResponse<Omit<DeviceLinkCompleteResult, "tempKeyPair">>(response);
  return {
    ...result,
    tempKeyPair,
  };
}

export async function applyLinkedDeviceSession(token: string, tempKeyPair?: TempKeyPair): Promise<void> {
  const trimmedToken = token.trim();
  if (!trimmedToken) {
    throw new Error("Missing linked-device token");
  }

  if (tempKeyPair) {
    await installKeyPair(tempKeyPair);
  }

  const store = useStore.getState();
  store.setApiToken(trimmedToken);
  store.setCommunityToken(trimmedToken);
  setToken(trimmedToken);
  await hydrateSelfUserFromToken(trimmedToken);
  const preBootstrapStore = useStore.getState() as any;
  console.info("[deviceLinking] before runBootstrap", {
    hasApiToken: !!preBootstrapStore.apiToken,
    hasKeyPair: !!preBootstrapStore.keyPair?.base64?.secretKey,
    currentUserId: currentUserIdFromStore() || null,
    selfPublicKey:
      preBootstrapStore.contactsById?.[currentUserIdFromStore() || ""]?.publicKey || null,
  });
  await runBootstrap(trimmedToken);
  const postBootstrapStore = useStore.getState() as any;
  console.info("[deviceLinking] after runBootstrap", {
    hasApiToken: !!postBootstrapStore.apiToken,
    hasKeyPair: !!postBootstrapStore.keyPair?.base64?.secretKey,
    currentUserId: currentUserIdFromStore() || null,
    selfPublicKey:
      postBootstrapStore.contactsById?.[currentUserIdFromStore() || ""]?.publicKey || null,
  });
}

function currentUserIdFromStore(): string | undefined {
  const store = useStore.getState() as any;
  return store.userId || (store.apiToken ? parseJwtPayload(store.apiToken)?.user_id : undefined);
}

function linkedDeviceContract() {
  return {
    kindField: linkedDeviceFieldKind(),
    notificationKind: linkedDeviceNotificationKind(),
    keySyncKind: linkedDeviceKeySyncKind(),
    deviceIdField: linkedDeviceFieldDeviceId(),
    tempPublicKeyField: linkedDeviceFieldTempPublicKey(),
    publicKeyField: linkedDeviceFieldPublicKey(),
    secretKeyField: linkedDeviceFieldSecretKey(),
    mlKemPublicKeyField: linkedDeviceFieldMlKemPublicKey(),
    mlKemSecretKeyField: linkedDeviceFieldMlKemSecretKey(),
    targetDeviceIdField: linkedDeviceFieldTargetDeviceId(),
  };
}

function parseKeySyncControlPayload(value: string | undefined): KeySyncControlPayload | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<KeySyncControlPayload>;
    const contract = linkedDeviceContract();
    const kind = parsed[contract.kindField as keyof typeof parsed];
    const publicKey = parsed[contract.publicKeyField as keyof typeof parsed];
    const secretKey = parsed[contract.secretKeyField as keyof typeof parsed];
    const mlKemPublicKey = parsed[contract.mlKemPublicKeyField as keyof typeof parsed];
    const mlKemSecretKey = parsed[contract.mlKemSecretKeyField as keyof typeof parsed];
    if (
      kind === contract.keySyncKind &&
      typeof publicKey === "string" &&
      publicKey.trim() &&
      typeof secretKey === "string" &&
      secretKey.trim()
    ) {
      return {
        kind: contract.keySyncKind,
        publicKey: publicKey.trim(),
        secretKey: secretKey.trim(),
        mlKemPublicKey:
          typeof mlKemPublicKey === "string" && mlKemPublicKey.trim()
            ? mlKemPublicKey.trim()
            : undefined,
        mlKemSecretKey:
          typeof mlKemSecretKey === "string" && mlKemSecretKey.trim()
            ? mlKemSecretKey.trim()
            : undefined,
      };
    }
  } catch {
    return null;
  }
  return null;
}

export function extractIncomingKeySyncPayload(value: string | undefined): KeySyncControlPayload | null {
  return parseKeySyncControlPayload(value);
}

export async function applyIncomingKeySync(payload: KeySyncControlPayload): Promise<void> {
  const store = useStore.getState() as any;
  const token = store.apiToken as string | undefined;
  const currentUserId = currentUserIdFromStore();
  const contract = linkedDeviceContract();
  const currentDeviceId =
    parseJwtPayload(token || "")?.device_id ||
    store.deviceId ||
    "current";

  console.info("[deviceLinking] applying incoming key sync", {
    currentUserId: currentUserId || null,
    publicKeyPrefix: payload.publicKey.slice(0, 12),
  });

  await db.updateKeyPair(
    payload.publicKey,
    payload.secretKey,
    payload.mlKemPublicKey,
    payload.mlKemSecretKey
  );
  store.setKeyPair?.({
    cryptoKeys: {
      publicKey: decodeBase64(payload.publicKey),
      secretKey: decodeBase64(payload.secretKey),
    },
    base64: {
      publicKey: payload.publicKey,
      secretKey: payload.secretKey,
    },
  });
  if (payload.mlKemPublicKey && payload.mlKemSecretKey) {
    store.setMlKemKeyPair?.({
      cryptoKeys: {
        publicKey: decodeBase64(payload.mlKemPublicKey),
        secretKey: decodeBase64(payload.mlKemSecretKey),
      },
      base64: {
        publicKey: payload.mlKemPublicKey,
        secretKey: payload.mlKemSecretKey,
      },
    });
  }

  if (currentUserId && store.contactsById?.[currentUserId]) {
    const existingUser = store.contactsById[currentUserId];
    const mergedPublicKeys = extractContactPublicKeys(existingUser);
    const nextPublicKeys = [
      ...mergedPublicKeys.filter((entry) => entry.deviceId !== currentDeviceId),
      {
        deviceId: currentDeviceId,
        publicKey: payload.publicKey,
        mlKemPublicKey: payload.mlKemPublicKey,
      },
    ];
    store.setUser?.({
      ...existingUser,
      publicKey: existingUser.publicKey || payload.publicKey,
      publicKeys: nextPublicKeys,
    });
  }

  if (!token) return;

  const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/update-public-key`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: buildBasicAuthHeader(token),
    },
    body: JSON.stringify({
      [contract.publicKeyField]: payload.publicKey,
      ...(payload.mlKemPublicKey
        ? { [contract.mlKemPublicKeyField]: payload.mlKemPublicKey }
        : {}),
    }),
  });

  await parseResponse(response);
}

export async function maybeSendLinkedDeviceKeySync(payload: Record<string, unknown> | undefined): Promise<boolean> {
  const candidate = payload as Partial<DeviceLinkedNotification> | undefined;
  const contract = linkedDeviceContract();
  const targetDeviceIdValue = candidate?.[contract.deviceIdField as keyof typeof candidate];
  const targetDeviceId =
    typeof targetDeviceIdValue === "string" && targetDeviceIdValue.trim()
      ? targetDeviceIdValue.trim()
      : "";
  const tempPublicKeyValue = candidate?.[contract.tempPublicKeyField as keyof typeof candidate];
  const tempPublicKey =
    typeof tempPublicKeyValue === "string" && tempPublicKeyValue.trim()
      ? tempPublicKeyValue.trim()
      : "";
  const kindValue = candidate?.[contract.kindField as keyof typeof candidate];
  const kind = typeof kindValue === "string" ? kindValue : "";

  if (kind !== contract.notificationKind || !targetDeviceId || !tempPublicKey) {
    return false;
  }

  const store = useStore.getState() as any;
  const currentUserId = currentUserIdFromStore();
  const secretKey = store.keyPair?.base64?.secretKey || store.keyPair?.base64?.secret_key;
  const publicKey = store.keyPair?.base64?.publicKey || store.keyPair?.base64?.public_key;
  const mlKemPublicKey =
    store.mlKemKeyPair?.base64?.publicKey ||
    store.mlKemKeyPair?.base64?.public_key ||
    store.appState?.mlKemPublicKey ||
    store.appState?.ml_kem_public_key;
  const mlKemSecretKey =
    store.mlKemKeyPair?.base64?.secretKey ||
    store.mlKemKeyPair?.base64?.secret_key ||
    store.appState?.mlKemSecretKey ||
    store.appState?.ml_kem_secret_key;

  if (!currentUserId || !secretKey || !publicKey) {
    console.warn("[deviceLinking] cannot send linked-device key sync", {
      hasCurrentUserId: !!currentUserId,
      hasSecretKey: !!secretKey,
      hasPublicKey: !!publicKey,
      targetDeviceId,
    });
    return false;
  }

  console.info("[deviceLinking] sending linked-device key sync", {
    currentUserId,
    targetDeviceId,
    tempPublicKeyPrefix: tempPublicKey.slice(0, 12),
    publicKeyPrefix: publicKey.slice(0, 12),
  });

  const prepared = prepareDMMessage(
    JSON.stringify({
      [contract.kindField]: contract.keySyncKind,
      [contract.publicKeyField]: publicKey,
      [contract.secretKeyField]: secretKey,
      ...(mlKemPublicKey && mlKemSecretKey
        ? {
            [contract.mlKemPublicKeyField]: mlKemPublicKey,
            [contract.mlKemSecretKeyField]: mlKemSecretKey,
          }
        : {}),
    }),
    currentUserId,
    tempPublicKey,
    currentUserId,
    secretKey,
    "SYSTEM"
  );
  const now = Date.now();

  sendWebSocket(WebSocketEventType.MESSAGE_SEND, {
    id: prepared.id,
    type: "SYSTEM",
    appId: appConfig.appId,
    senderId: currentUserId,
    receiverId: currentUserId,
    threadKey: prepared.thread_key,
    contentType: "SYSTEM",
    encryptedContent: prepared.encrypted_content,
    nonce: prepared.nonce,
    status: "SENT",
    createdAt: now,
    updatedAt: now,
    [contract.targetDeviceIdField]: targetDeviceId,
  });

  return true;
}

export async function listDevices(token: string): Promise<LinkedDevice[]> {
  const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/devices`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: buildBasicAuthHeader(token),
    },
  });

  const payload = await parseResponse<{ devices?: LinkedDevice[] }>(response);
  return Array.isArray(payload.devices) ? payload.devices : [];
}

export async function unlinkDevice(token: string, deviceId: string): Promise<void> {
  const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/devices/${deviceId}/unlink`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: buildBasicAuthHeader(token),
    },
    body: JSON.stringify({}),
  });

  await parseResponse(response);
}
